import React, { useEffect, useMemo, useRef, useState } from "react";
import { EditableSelectCell } from "../EditableSelectCell";
import { getFamilyTableOptions } from "./utils";
import { Checkbox, TextField } from "@mui/material";

// ✅ Proper React component — hooks are legal here
function FamilyTableCell({ row, handleFieldUpdate, fullSpecTable }) {
  const Component = row.original.Components;
  const spec = row.original.Spec;
  const value = row.original["Family Table"] || "";

  const schedRating = row.original["Schedule/Rating"];
  const ends = row.original["Ends"];
  const material = row.original["Material"];
  const addNotes = row.original["Additional Notes"];

  const [fullOptions, setFullOptions] = useState([]);

  useEffect(() => {
    let isCancelled = false;

    const loadFamilyOptions = async () => {
      if (!Component || !spec) {
        setFullOptions([]);
        return;
      }

      try {
        const options = await getFamilyTableOptions(
          Component,
          spec,
          {
            "Schedule/Rating": schedRating,
            Ends: ends,
            Material: material,
            "Additional Notes": addNotes,
          },
          fullSpecTable,
        );

        if (isCancelled) return;

        setFullOptions(Array.isArray(options) ? options : []);
      } catch (error) {
        console.error("[FamilyTableCell] Failed to load family options:", error);

        if (!isCancelled) {
          setFullOptions([]);
        }
      }
    };

    loadFamilyOptions();

    return () => {
      isCancelled = true;
    };
  }, [
    Component,
    spec,
    schedRating,
    ends,
    material,
    addNotes,
    fullSpecTable,
  ]);

  const nameOptions = useMemo(() => {
    const seen = new Set();

    return fullOptions
      .map((cur) => String(cur?.Family_Name ?? "").trim())
      .filter((n) => {
        if (!n || n === "-") return false;
        if (seen.has(n)) return false;

        seen.add(n);
        return true;
      });
  }, [fullOptions]);

  const safeValue = nameOptions.includes(value) ? value : "";

  const lastAutoSelected = useRef(null);
  const lastTableTarget = useRef(null);

  useEffect(() => {
    if (!Component) return;
    if (nameOptions.length !== 1) return;

    const onlyOption = nameOptions[0];

    if (value === onlyOption) return;
    if (lastAutoSelected.current === onlyOption) return;

    lastAutoSelected.current = onlyOption;

    handleFieldUpdate(row, "Family Table", onlyOption);
  }, [Component, nameOptions, value, row, handleFieldUpdate]);

  useEffect(() => {
    const tableTarget =
      fullOptions.find((cur) => cur.Family_Name === safeValue)?.Family_Table ||
      "";

    if (row.original.FamilyTableTarget === tableTarget) return;
    if (lastTableTarget.current === tableTarget) return;

    lastTableTarget.current = tableTarget;

    handleFieldUpdate(row, "FamilyTableTarget", tableTarget);
  }, [safeValue, fullOptions, row, handleFieldUpdate]);

  if (!Component) return <EditableSelectCell disabled />;

  return (
    <EditableSelectCell
      value={safeValue}
      options={nameOptions}
      onChange={(newVal) => handleFieldUpdate(row, "Family Table", newVal)}
    />
  );
}

// ✅ Pure function — no hooks inside
export const columns = (
  allTables,
  handleFieldUpdate,
  handleDeleteRow,
  handleToggleRow,
  fullSpecTable,
) => [
  {
    id: "select",
    header: "Select",
    Cell: ({ row }) => {
      const rowData = row.original;
      return (
        <Checkbox
          checked={rowData.select}
          onChange={(e) => {
            handleFieldUpdate(row, "select", e.target.checked);
            handleToggleRow(rowData, e.target.checked);
          }}
        />
      );
    },
    size: 20,
  },
  {
    accessorKey: "Spec",
    header: "Spec",
    enableColumnActions: false,
    size: 40,
  },
  {
    accessorKey: "Components",
    header: "Component",
    enableColumnActions: false,
    size: 60,
  },
  {
    accessorKey: "Size",
    header: "Size",
    enableColumnActions: false,
    size: 20,
  },
  {
    accessorKey: "Schedule/Rating",
    header: "Sch/Rating",
    enableColumnActions: false,
    size: 20,
    Header: () => (
      <span
        style={{
          display: "inline-block",
          whiteSpace: "normal",
          overflowWrap: "break-word",
        }}
      >
        Sch/Rating
      </span>
    ),
  },
  {
    accessorKey: "Ends",
    header: "Ends",
    enableColumnActions: false,
    size: 20,
  },
  // {
  //   accessorKey: "Dimensional Standard",
  //   header: "Dimensional Standard",
  //   enableColumnActions: false,
  //   size: 90,
  // },
  {
    accessorKey: "Material",
    header: "Material",
    enableColumnActions: false,
    size: 40,
  },
  {
    accessorKey: "Additional Notes",
    header: "Additional Notes",
    enableColumnActions: false,
    size: 125,
  },
  {
    accessorKey: "Material Description",
    header: "Material Description",
    enableColumnActions: false,
    size: 180,
  },
  {
    accessorKey: "Material Code",
    header: "Material Code",
    enableColumnActions: false,
    size: 90,
    Cell: ({ row }) => {
      const value = row.original.MaterialCode || "-";
      const [inputValue, setInputValue] = React.useState(value);

      return (
        <TextField
          variant="standard"
          size="small"
          fullWidth
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={() => {
            if (inputValue !== value) {
              handleFieldUpdate(row, "MaterialCode", inputValue);
              handleToggleRow(row.original, true);
            }
          }}
        />
      );
    },
  },
  {
    accessorKey: "Family Table",
    header: "Family Table",
    enableColumnActions: false,
    size: 150,
    Cell: ({ row }) => (
      <FamilyTableCell
        row={row}
        allTables={allTables}
        handleFieldUpdate={handleFieldUpdate}
        fullSpecTable={fullSpecTable}
      />
    ),
  },
];
